import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import api, { getAllAuditLogs, getUserAuditLogs } from '../services/api';
import type { FormattedAuditLog } from '../types';

interface TailLogEntry {
  timestamp: string;
  level: string;
  message: string;
}

type AuditLogEntry = FormattedAuditLog;

const SystemLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<TailLogEntry[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [reviewed, setReviewed] = useState<number>(0);
  const [executed, setExecuted] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  // Persist active tab to localStorage
  const getInitialTab = (): 'system' | 'audit' => {
    const savedTab = localStorage.getItem('systemLogsActiveTab') as 'system' | 'audit' | null;
    if (savedTab && ['system', 'audit'].includes(savedTab)) {
      return savedTab;
    }
    return 'audit'; // Default to My Activity
  };
  
  const [activeTab, setActiveTab] = useState<'system' | 'audit'>(getInitialTab());
  
  const handleTabChange = (tab: 'system' | 'audit') => {
    setActiveTab(tab);
    localStorage.setItem('systemLogsActiveTab', tab);
  };

  // Helper to get event type display properties
  const getEventTypeStyle = (eventType: string, fallbackLabel: string) => {
    const styles: { [key: string]: { bg: string; text: string; label: string } } = {
      'AUTOMATION_ENABLED': { bg: 'bg-green-900/30', text: 'text-green-300', label: 'Automation Enabled' },
      'AUTOMATION_DISABLED': { bg: 'bg-red-900/30', text: 'text-red-300', label: 'Automation Disabled' },
      'SCREENING_STARTED': { bg: 'bg-blue-900/30', text: 'text-blue-300', label: 'Screening Started' },
      'SCREENING_COMPLETED': { bg: 'bg-blue-900/30', text: 'text-blue-300', label: 'Screening Done' },
      'TRADE_SIGNAL': { bg: 'bg-purple-900/30', text: 'text-purple-300', label: 'Trade Signal' },
      'ORDER_PLACED': { bg: 'bg-cyan-900/30', text: 'text-cyan-300', label: 'Order Placed' },
      'ORDER_FILLED': { bg: 'bg-green-900/30', text: 'text-green-300', label: 'Order Filled' },
      'ORDER_CANCELED': { bg: 'bg-yellow-900/30', text: 'text-yellow-300', label: 'Order Canceled' },
      'ORDER_REJECTED': { bg: 'bg-red-900/30', text: 'text-red-300', label: 'Order Rejected' },
      'RISK_ALERT': { bg: 'bg-orange-900/30', text: 'text-orange-300', label: 'Risk Alert' },
      'POSITION_OPENED': { bg: 'bg-emerald-900/30', text: 'text-emerald-300', label: 'Position Opened' },
      'POSITION_CLOSED': { bg: 'bg-amber-900/30', text: 'text-amber-300', label: 'Position Closed' },
      'SETTINGS_UPDATED': { bg: 'bg-indigo-900/30', text: 'text-indigo-300', label: 'Settings Changed' },
      'ACCOUNT_CREATED': { bg: 'bg-teal-900/30', text: 'text-teal-300', label: 'Account Created' },
      'ACCOUNT_UPDATED': { bg: 'bg-teal-900/30', text: 'text-teal-300', label: 'Account Updated' },
      'PASSWORD_RESET_REQUESTED': { bg: 'bg-amber-900/30', text: 'text-amber-200', label: 'Password Reset Requested' },
      'PASSWORD_RESET_COMPLETED': { bg: 'bg-green-900/30', text: 'text-green-200', label: 'Password Reset Completed' },
      'PASSWORD_CHANGED': { bg: 'bg-green-900/30', text: 'text-green-200', label: 'Password Changed' },
      'EMAIL_VERIFIED': { bg: 'bg-blue-900/30', text: 'text-blue-200', label: 'Email Verified' },
    };
    return styles[eventType] || { bg: 'bg-neutral-900/30', text: 'text-neutral-300', label: fallbackLabel };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const promises = [
        api.get('/logs/tail?limit=200'),
        api.get('/logs/daily-review'),
      ];

      // Add audit logs fetch for all users
      if (user?.role === 'ADMIN') {
        promises.push(getAllAuditLogs({ limit: 50 }) as any);
      } else {
        promises.push(getUserAuditLogs({ limit: 50 }) as any);
      }

      const responses = await Promise.all(promises);
      
      setLogs(Array.isArray(responses[0]?.data?.data?.logs) ? responses[0].data.data.logs : []);
      setReviewed(Number(responses[1]?.data?.data?.reviewed || 0));
      setExecuted(Number(responses[1]?.data?.data?.executed || 0));

      // Set audit logs for all users
      if (responses[2]) {
        const auditResponse = responses[2] as { success?: boolean; data?: { auditLogs?: AuditLogEntry[] } };
        if (auditResponse?.success && Array.isArray(auditResponse.data?.auditLogs)) {
          setAuditLogs(auditResponse.data.auditLogs);
        } else if (Array.isArray((auditResponse as any)?.data?.auditLogs)) {
          setAuditLogs((auditResponse as any).data.auditLogs);
        } else {
          setAuditLogs([]);
        }
      }
    } catch (e) {
      console.error('Failed to load logs', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white">Activity & Logs</h1>
            <p className="text-sm text-neutral-400">Live tail logs and daily trading activity</p>
            
            {/* Tabs - conditional based on role */}
            <div className="mt-4 flex space-x-4">
              {/* My Activity - Always shown */}
              <button
                onClick={() => handleTabChange('audit')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'audit'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                My Activity
              </button>
              
              {/* System Logs - Admin only */}
              {user?.role === 'ADMIN' && (
                <button
                  onClick={() => handleTabChange('system')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'system'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-neutral-800 text-neutral-400 hover:text-white'
                  }`}
                >
                  System Logs
                </button>
              )}
            </div>
          </div>

          {/* Daily Activity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
              <p className="text-sm text-neutral-400">Reviewed Today</p>
              <p className="text-3xl font-bold text-white">{reviewed}</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
              <p className="text-sm text-neutral-400">Executed Today</p>
              <p className="text-3xl font-bold text-white">{executed}</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6">
              <p className="text-sm text-neutral-400">Refresh</p>
              <button onClick={fetchData} className="mt-2 px-3 py-2 bg-cyan-700 hover:bg-cyan-600 rounded text-white">Refresh Now</button>
            </div>
          </div>

          {/* System Logs */}
          {activeTab === 'system' && (
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">System Logs</h2>
                <span className="text-xs text-neutral-400">Auto-refreshing every 5s</span>
              </div>
              <div className="p-6">
                {loading ? (
                  <div className="text-neutral-400">Loading logs...</div>
                ) : logs.length === 0 ? (
                  <div className="text-neutral-400">No logs to display.</div>
                ) : (
                  <div className="max-h-[60vh] overflow-auto font-mono text-sm space-y-1">
                    {logs.map((l, idx) => (
                      <div key={idx} className="text-neutral-300">
                        <span className="text-neutral-500">{new Date(l.timestamp).toLocaleTimeString()}</span>
                        <span className={`ml-2 ${l.level.includes('error') ? 'text-red-400' : l.level.includes('warn') ? 'text-yellow-400' : 'text-green-400'}`}>[{l.level}]</span>
                        <span className="ml-2 text-neutral-200">{l.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Logs - Admin sees all users, Regular users see their own */}
          {activeTab === 'audit' && (
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {user?.role === 'ADMIN' ? 'All User Activity' : 'My Activity'}
                </h2>
                <span className="text-xs text-neutral-400">
                  {user?.role === 'ADMIN' ? 'Activity across all users' : 'Your trading activity and decisions'}
                </span>
              </div>
              <div className="p-6">
                {loading ? (
                  <div className="text-neutral-400">Loading your activity...</div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-neutral-400 mb-4">No activity yet</div>
                    <div className="text-sm text-neutral-500">
                      {user?.role === 'ADMIN' 
                        ? 'Activity from all users will appear here when automation runs during market hours (9:45 AM - 3:30 PM ET, Mon-Fri)'
                        : 'Your trading activity will appear here once automation runs during market hours (9:45 AM - 3:30 PM ET, Mon-Fri)'}
                    </div>
                  </div>
                ) : (
                  <div className="max-h-[60vh] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-800">
                        <tr>
                          <th className="px-4 py-2 text-left text-neutral-300">Timestamp</th>
                          {user?.role === 'ADMIN' && <th className="px-4 py-2 text-left text-neutral-300">User</th>}
                          <th className="px-4 py-2 text-left text-neutral-300">Event Type</th>
                          <th className="px-4 py-2 text-left text-neutral-300">IP Address</th>
                          <th className="px-4 py-2 text-left text-neutral-300">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-700">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-neutral-800/50 align-top">
                            <td className="px-4 py-2 text-neutral-300">
                              <div className="font-medium text-white">{log.timestampDisplay}</div>
                              <div className="text-xs text-neutral-500">{new Date(log.timestampIso).toLocaleString()}</div>
                            </td>
                            {user?.role === 'ADMIN' && (
                              <td className="px-4 py-2">
                                <div className="text-white">
                                  <div className="font-medium">
                                    {log.user ? `${log.user.firstName ?? ''} ${log.user.lastName ?? ''}`.trim() || 'Unknown User' : 'Unknown User'}
                                  </div>
                                  <div className="text-xs text-neutral-400">
                                    {log.user?.email || 'Unknown Email'}
                                  </div>
                                  <div className="text-xs text-neutral-500">
                                    {log.user?.role || 'Unknown Role'}
                                  </div>
                                </div>
                              </td>
                            )}
                            <td className="px-4 py-2">
                              {(() => {
                                const style = getEventTypeStyle(log.eventType, log.eventLabel);
                                return (
                                  <span className={`px-2 py-1 ${style.bg} ${style.text} rounded text-xs font-medium`}>
                                    {style.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2 text-neutral-300">
                              <div className="text-white">{log.ip.display}</div>
                              {user?.role === 'ADMIN' && log.ip.raw && (
                                <div className="text-xs text-neutral-500">{log.ip.raw}</div>
                              )}
                            </td>
                            <td className="px-4 py-2 text-neutral-300">
                              <div className="max-w-md space-y-1">
                                <div className="text-white">{log.details.summary}</div>
                                {log.details.items.length > 0 && (
                                  <ul className="text-xs text-neutral-400 list-disc list-inside space-y-0.5">
                                    {log.details.items.map((item) => (
                                      <li key={`${log.id}-${item.label}`}>
                                        <span className="text-neutral-300">{item.label}:</span> {item.value}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SystemLogs;
